import { NarrationBudget } from '../../api/narration';
import { ClipPrompt } from '../../api/structs/clip';
import { MediaItem } from '../../api/structs/media';
import { EDITOR_FPS, stateFromClip } from '../clip-editor/editorStore';

/**
 * How a narration take sits against the clip it has to fit into, and the two
 * ways out when it does not: regenerate faster, or shorten the text.
 *
 * The clip's length is taken from the editor's own timeline, so the number the
 * modal compares against is the number the preview plays and the render makes.
 */

export type NarrationFitState = 'ok' | 'long' | 'short';

/** The speed window a "Fit to clip" regeneration may use. */
export const FIT_SPEED_MIN = 0.85;
export const FIT_SPEED_MAX = 1.25;

const OK_DRIFT = 0.1;
const SHORT_DRIFT = 0.2;

export const CALIBRATION_MEASURED =
  'Rates are measured from your previous takes for this voice and speed.';
export const CALIBRATION_DEFAULT =
  'No measurement yet for this voice and speed — using the default rate.';
export const FIT_OUT_OF_RANGE_HINT =
  'Needs a speed outside 0.85–1.25 — shorten the text instead';

export const clipDurationSeconds = (clip: ClipPrompt): number => {
  const { timeline } = stateFromClip(clip);
  // Remotion cannot render a zero-frame composition, so an empty clip's
  // timeline floors at one frame. That is not a length — it is "no slides yet".
  return timeline.entries.length === 0 ? 0 : timeline.totalDurationInFrames / EDITOR_FPS;
};

/**
 * The take the clip narrates with, resolved exactly as the narrator track
 * resolves it (NarratorTrack.tsx): the bound id when the clip names one — and
 * nothing else, so a measurement can never come from another item — else the
 * first audio item that says it is narration.
 */
export const narratorTakeItem = (clip: ClipPrompt): MediaItem | null => {
  const audios = clip.media?.audios || [];
  const boundId = (clip.metadata?.narrator_media_id || clip.metadata?.narratorMediaId) as
    | string
    | undefined;
  return boundId
    ? audios.find((item) => item.id === boundId) || null
    : audios.find((item) => item.metadata?.role === 'narrator') || null;
};

/** Within ±10 % reads fine; longer than the clip is the real problem; far short is wasted room. */
export const narrationFitState = (takeSeconds: number, clipSeconds: number): NarrationFitState => {
  if (!(clipSeconds > 0)) return 'ok';
  const drift = (takeSeconds - clipSeconds) / clipSeconds;
  if (Math.abs(drift) <= OK_DRIFT) return 'ok';
  if (drift > 0) return 'long';
  return drift < -SHORT_DRIFT ? 'short' : 'ok';
};

/** One decimal at most: "34", "33.5". */
export const formatFitSeconds = (seconds: number): string => {
  const rounded = Math.round(seconds * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/** The speed a regeneration needs to land the take on the clip, at two decimals. */
export const fitSpeed = (
  takeSeconds: number,
  clipSeconds: number,
  currentSpeed: number
): number | null => {
  if (!(clipSeconds > 0) || !(takeSeconds > 0)) return null;
  return Math.round((takeSeconds / clipSeconds) * currentSpeed * 100) / 100;
};

export const isFitSpeedAllowed = (speed: number | null): boolean =>
  speed !== null && speed >= FIT_SPEED_MIN && speed <= FIT_SPEED_MAX;

export const countWords = (text: string): number => {
  const trimmed = text.trim();
  return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
};

export const budgetSourceLabel = (budget: NarrationBudget): string =>
  budget.source === 'measured'
    ? `measured from ${budget.samples} take${budget.samples === 1 ? '' : 's'}`
    : 'default rate';
